import { Controller } from "@hotwired/stimulus"

// Dispatch gate for the EmailCampaign show page (#528).
//
// Dispatch is the only action that mails the whole base, so the UI layers two
// explicit confirmations on top of the server-side guards (super_admin + TOTP +
// confirmed_count match, #529):
//
//   1. A FRESH live count is fetched on load and written into the hidden
//      `confirmed_count` the form submits. If the audience drifted since the
//      operator last looked, the backend rejects the stale number — but we also
//      keep the displayed number current so the human sees the real size.
//   2. The submit stays DISABLED until the operator ticks the acknowledgement
//      checkbox ("I understand this emails N people"). On submit, the project's
//      turbo-confirm modal (danger variant) asks once more. Two deliberate
//      gestures + the TOTP field = no accidental blast.
export default class extends Controller {
  static targets = ["confirmedCount", "countDisplay", "acknowledge", "submit", "form"]
  static values = { previewUrl: String, audienceConfig: String }

  connect() {
    this.updateGate()
    this.refreshCount()
  }

  async refreshCount() {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')
      const body = new URLSearchParams()
      body.set("audience_config", this.audienceConfigValue || "{}")

      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": token ? token.content : "",
          "Accept": "application/json"
        },
        body
      })

      if (!response.ok) return

      const data = await response.json()
      this.applyCount(data.count)
    } catch (_e) {
      // Leave the server-rendered count in place; the backend count match still
      // protects the dispatch.
    }
  }

  applyCount(count) {
    const value = String(count)
    if (this.hasConfirmedCountTarget) this.confirmedCountTarget.value = value
    if (this.hasCountDisplayTarget) this.countDisplayTarget.textContent = value

    // Surface the live count inside the confirmation copy + the acknowledgement
    // label so both confirmations name the real audience size.
    if (this.hasFormTarget) {
      const tpl = this.formTarget.dataset.confirmTemplate
      if (tpl) this.formTarget.dataset.turboConfirm = tpl.replace("%{count}", value)
    }
    if (this.hasAcknowledgeTarget) {
      const label = this.acknowledgeTarget.closest("label")
      if (label && label.dataset.template) {
        label.querySelector("[data-ack-text]").textContent =
          label.dataset.template.replace("%{count}", value)
      }
    }
    this.updateGate()
  }

  // Submit is enabled only when the operator has explicitly acknowledged.
  updateGate() {
    if (!this.hasSubmitTarget) return
    const acknowledged = this.hasAcknowledgeTarget ? this.acknowledgeTarget.checked : true
    this.submitTarget.disabled = !acknowledged
  }
}
